import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PromoCode } from '../services/api';

interface PromoCodeViewModalProps {
  visible: boolean;
  promoCode: PromoCode | null;
  onCancel: () => void;
}

const PromoCodeViewModal: React.FC<PromoCodeViewModalProps> = ({
  visible,
  promoCode,
  onCancel,
}) => {
  if (!visible || !promoCode) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDiscount = (type: string, value: number) => {
    if (type === 'percentage') {
      return `${value}%`;
    }
    return formatCurrency(value);
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Promo Code Details</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">ID</label>
                <p className="text-sm text-gray-900 mt-1">{promoCode.id}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    promoCode.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {promoCode.isActive ? 'Active' : 'Inactive'}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Promo Code */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Promo Code</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-2xl font-bold font-mono text-blue-900">{promoCode.code}</p>
            </div>
          </div>

          {/* Promo Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Promo Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-sm text-gray-900 mt-1 font-semibold">{promoCode.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Discount</label>
                <p className="text-sm text-gray-900 mt-1 font-semibold">
                  {formatDiscount(promoCode.discountType, Number(promoCode.discountValue))}
                </p>
                <p className="text-xs text-gray-500 mt-1">{promoCode.discountType}</p>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-500">Description</label>
                <p className="text-sm text-gray-900 mt-1">{promoCode.description}</p>
              </div>
            </div>
          </div>

          {/* Discount Limits */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Discount Limits</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Max Discount</label>
                <p className="text-sm text-gray-900 mt-1">
                  {promoCode.maxDiscount ? formatCurrency(Number(promoCode.maxDiscount)) : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Min Purchase</label>
                <p className="text-sm text-gray-900 mt-1">
                  {promoCode.minPurchase ? formatCurrency(Number(promoCode.minPurchase)) : 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Period */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Period</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Start Date</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(promoCode.startDate)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">End Date</label>
                <p className="text-sm text-gray-900 mt-1">{formatDate(promoCode.endDate)}</p>
              </div>
            </div>
          </div>

          {/* Usage Limits */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Limits</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Usage Limit</label>
                <p className="text-sm text-gray-900 mt-1">
                  {promoCode.usageLimit || 'Unlimited'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Per User Limit</label>
                <p className="text-sm text-gray-900 mt-1">
                  {promoCode.perUserLimit || 'Unlimited'}
                </p>
              </div>
            </div>
          </div>

          {/* Products */}
          {promoCode.products && promoCode.products.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Products ({promoCode.products.length})
              </h3>
              <div className="space-y-2">
                {promoCode.products.map((product) => (
                  <div key={product.id} className="bg-gray-50 p-3 rounded-md">
                    <div className="text-sm font-medium text-gray-900">
                      {product.product?.name || `Product ID: ${product.productId}`}
                    </div>
                    {product.product?.slug && (
                      <div className="text-xs text-gray-500 mt-1">{product.product.slug}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dates */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Dates</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Created At</label>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(promoCode.createdAt).toLocaleString('id-ID')}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Updated At</label>
                <p className="text-sm text-gray-900 mt-1">
                  {new Date(promoCode.updatedAt).toLocaleString('id-ID')}
                </p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-imola-primary text-white rounded-md hover:bg-imola-primary/80"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCodeViewModal;

